import React from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FormDatePicker } from '../../experienceSettings/formComponents/FormDatePicker';
import { DATE_FORMAT } from '../../calendar/calendarWrapper/CalendarWrapper';

type PauseScheduleDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (dates: { pause_start_date: string; pause_end_date: string }) => Promise<void>;
  initialStart?: string;
  initialEnd?: string;
  lockStart?: boolean;
};

type PauseFormValues = {
  pause_start_date: string;
  pause_end_date: string;
};

const PauseScheduleDialog = ({
  open,
  onClose,
  onSubmit,
  initialStart,
  initialEnd,
  lockStart = false,
}: PauseScheduleDialogProps) => {
  const { t } = useTranslation();
  const isEdit = !!(initialStart || initialEnd);
  const today = moment().format(DATE_FORMAT);

  const initialValues: PauseFormValues = {
    pause_start_date: initialStart ? moment(initialStart).format(DATE_FORMAT) : today,
    pause_end_date: initialEnd ? moment(initialEnd).format(DATE_FORMAT) : moment().add(30, 'days').format(DATE_FORMAT),
  };

  const getErrorKey = ({ pause_start_date, pause_end_date }: PauseFormValues): string | null => {
    if (!lockStart && moment(pause_start_date).isBefore(today)) return 'passes.recurring.pauseErrors.startInPast';
    if (moment(pause_end_date).isBefore(today)) return 'passes.recurring.pauseErrors.endInPast';
    if (!moment(pause_end_date).isAfter(moment(pause_start_date))) return 'passes.recurring.pauseErrors.endBeforeStart';
    return null;
  };

  const titleKey = isEdit ? 'passes.recurring.pauseEditTitle' : 'passes.recurring.pauseScheduleTitle';
  const ctaKey = isEdit ? 'save' : 'passes.recurring.pause';

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { minWidth: { xs: '90%', sm: 420 } } }}>
      <DialogTitle>{t(titleKey)}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={async (values) => {
          await onSubmit(values);
          onClose();
        }}>
        {({ values, isSubmitting }) => {
          const errorKey = getErrorKey(values);
          return (
            <Form>
              <DialogContent>
                <Grid container flexDirection="column" gap={2} pt={1}>
                  <Grid container flexDirection="column">
                    <FormDatePicker
                      fieldName="pause_start_date"
                      label={t('passes.recurring.pauseStartLabel')}
                      disabled={lockStart}
                      disablePast={!lockStart}
                      isFieldUpdate
                      size="small"
                      fullWidth
                    />
                    {lockStart && (
                      <Typography variant="caption" color="text.secondary">
                        {t('passes.recurring.pauseStartLockedHint')}
                      </Typography>
                    )}
                  </Grid>
                  <FormDatePicker
                    fieldName="pause_end_date"
                    label={t('passes.recurring.pauseEndLabel')}
                    minDate={moment(lockStart ? today : values.pause_start_date).add(1, 'day')}
                    isFieldUpdate
                    size="small"
                    fullWidth
                  />
                  {errorKey && <Alert severity="error">{t(errorKey)}</Alert>}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>
                  {t('cancel')}
                </Button>
                <LoadingButton loading={isSubmitting} type="submit" variant="contained" disabled={!!errorKey}>
                  {t(ctaKey)}
                </LoadingButton>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default PauseScheduleDialog;
